import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { Row, Col, Button } from 'react-bootstrap';
import CustomScrollTrigger from 'js/wrapper/CustomScrollTrigger';
import { Parallax } from 'react-scroll-parallax';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { getSalutationOptions } from 'helpers/clients';
import { useAbstractCmsElement } from 'components/cms/hooks/useAbstractCmsElementNew';


import FormInput from 'components/forms/FormInput';
import FormAutocomplete from 'components/forms/FormInput'; // Import wie im Original
import FormTextarea from 'components/forms/FormTextarea';
import RichEditorContent from 'components/RichEditorContent';
import ModalPrivacyPolicy from 'components/modals/ModalPrivacyPolicy';

function CmsElementLeadForm(props) {
  const { editMode, index, showElementSettings } = props;

  // Intl Hook
  const intl = useIntl();
  const messages = intl?.messages || {};

  // CMS Logic Hook
  const {
    shown,
    setShown,
    getMainId,
    getCustomId,
    getParamValue,
    getBackgroundStyle,
    elementStyleTag,
    getParallaxData,
    getAppendHtml,
    cssClasses,
    responsiveClasses,
  } = useAbstractCmsElement(props);

  // dnd-kit Sortable Hook
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: index,
    disabled: !editMode,
  });

  // Local State
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);

  // Handlers
  const handleShowPrivacyPolicy = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowPrivacyPolicy(true);
  };

  const handleClosePrivacyPolicy = () => {
    setShowPrivacyPolicy(false);
  };

  // Hilfsfunktion für den Datenschutz-Text mit injiziertem Link
  const renderPrivacyPolicyText = () => {
    const text = messages.contact_agreement_text || '';
    if (!text) return null;

    const parts = text.split('{data_use}');
    if (parts.length === 1) return text;

    return (
      <>
        {parts[0]}
        <a href="#dataUse" onClick={handleShowPrivacyPolicy}>
          {messages.signup_agreement_text_data_use}
        </a>
        {parts[1]}
      </>
    );
  };

  // Styles & Config
  const dndStyle = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
    position: 'relative',
    width: '100%',
    cursor: editMode ? 'move' : 'default',
  };

  const isVisible = editMode || shown;
  const shownClass = isVisible ? 'elem-shown' : 'elem-hidden';
  const textClass = `text-${getParamValue('mainColor', 'master')}`;

  const withAddress = getParamValue('requireAddress', 'no') === 'yes';
  const withEmailContact = getParamValue('requireEmailContact') === 'yes';
  const withPhoneContact = getParamValue('requirePhoneContact') === 'yes';
  const withMessage = getParamValue('requireMessage') === 'yes';

  const parallaxData = getParallaxData();

  const innerContent = (
    <div
      id={getMainId()}
      className={`ms-lead-form-content ${shownClass} ${cssClasses} ${responsiveClasses}`.trim()}
      style={getBackgroundStyle()}
      onClick={(e) => editMode && showElementSettings(e, index)}
    >
      {elementStyleTag}

      <div className="lead-form-container">
        <h4 className={`no-margin p-b-5 ${textClass}`}>{getParamValue('title', '')}</h4>

        <RichEditorContent content={`<p class="${textClass}">${getParamValue('infosTop', '')}</p>`} />

        <div className="form-group-attached">
          <Row className="clearfix no-margin">
            <Col sm={6}>
              <FormAutocomplete
                label={messages.salutation}
                name="salutation"
                value="mr"
                options={getSalutationOptions(messages)}
              />
            </Col>
          </Row>
          <Row className="clearfix no-margin">
            <Col sm={6}>
              <FormInput label={messages.title} name="title" />
            </Col>
          </Row>

          <Row className="clearfix no-margin">
            <Col sm={6}>
              <FormInput label={messages.first_name} />
            </Col>
            <Col sm={6}>
              <FormInput label={messages.last_name} />
            </Col>
          </Row>

          {withAddress && (
            <>
              <FormInput label={messages.street} />
              <Row className="clearfix no-margin">
                <Col sm={6}>
                  <FormInput label={messages.house_number} />
                </Col>
                <Col sm={6}>
                  <FormInput label={messages.apartment_number} />
                </Col>
              </Row>
              <Row className="clearfix no-margin">
                <Col sm={6}>
                  <FormInput label={messages.zip_code} />
                </Col>
                <Col sm={6}>
                  <FormInput label={messages.city} />
                </Col>
              </Row>
            </>
          )}
        </div>

        {(withEmailContact || withPhoneContact) && (
          <div className="m-t-15 form-group-attached">
            {withEmailContact && <FormInput label={messages.email} />}
            {withPhoneContact && <FormInput type="number" label={messages.phone} />}
          </div>
        )}

        {withMessage && (
          <div className="m-t-15 form-group-attached">
            <FormTextarea label={messages.message} name="message" />
          </div>
        )}

        <RichEditorContent content={`<p class="${textClass}">${getParamValue('infosBottom', '')}</p>`} />

        <p className={textClass}>{renderPrivacyPolicyText()}</p>

        <div className="text-right">
          <Button className="call-to-action btn-animated from-left fa fa-arrow-right" variant="primary">
            <span>{getParamValue('buttonTitle', '')}</span>
          </Button>
        </div>
      </div>

      {getAppendHtml()}

      <ModalPrivacyPolicy show={showPrivacyPolicy} close={handleClosePrivacyPolicy} />
    </div>
  );

  return (
    <div ref={setNodeRef} style={dndStyle} {...attributes} {...listeners}>
      <div>
        <CustomScrollTrigger className="cms-overflow-fix" onEnter={() => setShown(true)} {...getCustomId()}>
          {parallaxData ? (
            <Parallax {...parallaxData}>
              {innerContent}
            </Parallax>
          ) : (
            innerContent
          )}
        </CustomScrollTrigger>
      </div>
    </div>
  );
}

export default CmsElementLeadForm;
